import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Layers as BayIcon,
  GridOn as RackIcon,
  Warehouse as WarehouseIcon
} from '@mui/icons-material';
import api from '../services/api.service';

export default function BayManager() {
  const [bays, setBays] = useState([]);
  const [racks, setRacks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingBay, setEditingBay] = useState(null);
  const [bayToDelete, setBayToDelete] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rack_id: '',
    warehouse_id: '',
    isActive: true
  });

  // Load bays, racks and warehouses on mount
  useEffect(() => {
    loadBays();
    loadRacks();
    loadWarehouses();
  }, []);

  const loadBays = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/inventories/bays');
      console.log('[BAY MANAGER DEBUG] Bays API response:', response);
      const baysData = response.data?.data || response.data || [];
      console.log('[BAY MANAGER DEBUG] Extracted bays data:', baysData);
      setBays(baysData);
    } catch (err) {
      setError('Failed to load bays');
      console.error('Error loading bays:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRacks = async () => {
    try {
      const response = await api.get('/inventories/racks');
      const racksData = response.data?.data || response.data || [];
      setRacks(racksData);
    } catch (err) {
      console.error('Error loading racks:', err);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await api.get('/inventories/warehouses');
      const warehousesData = response.data?.data?.warehouses || response.data?.warehouses || [];
      setWarehouses(warehousesData);
    } catch (err) {
      console.error('Error loading warehouses:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };



  const handleAddBay = () => {
    setEditingBay(null);
    setFormData({
      name: '',
      description: '',
      rack_id: '',
      warehouse_id: '',
      isActive: true
    });
    setDialogOpen(true);
  };

  const handleEditBay = (bay) => {
    setEditingBay(bay);
    setFormData({
      name: bay.name,
      description: bay.description || '',
      rack_id: bay.rack_id || '',
      warehouse_id: bay.warehouse_id || '',
      isActive: bay.isActive !== false
    });
    setDialogOpen(true);
  };

  const handleDeleteBay = (bay) => {
    setBayToDelete(bay);
    setDeleteDialogOpen(true);
  };

  const handleSaveBay = async () => {
    try {
      setError(null);
      
      if (!formData.name.trim()) {
        setError('Bay name is required');
        return;
      }
      
      const bayData = {
        name: formData.name,
        description: formData.description,
        isActive: formData.isActive
      };
      
      if (editingBay) {
        // Update existing bay
        await api.put(`/inventories/bays/${editingBay.id}`, bayData);
        setSuccess('Bay updated successfully');
      } else {
        // Create new bay
        await api.post('/inventories/bays', bayData);
        setSuccess('Bay created successfully');
      }
      
      setDialogOpen(false);
      loadBays();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save bay');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setError(null);
      await api.delete(`/inventories/bays/${bayToDelete.id}`);
      setSuccess('Bay deleted successfully');
      setDeleteDialogOpen(false);
      setBayToDelete(null);
      loadBays();
    } catch (err) {
      if (err.response?.data?.itemsCount > 0) {
        setError(`Cannot delete bay: It has ${err.response.data.itemsCount} items. Please reassign items first.`);
      } else {
        setError(err.response?.data?.error || 'Failed to delete bay');
      }
      setDeleteDialogOpen(false);
    }
  };

  const handleToggleActive = async (bay) => {
    try {
      await api.put(`/inventories/bays/${bay.id}`, {
        ...bay,
        isActive: !bay.isActive
      });
      loadBays();
    } catch (err) {
      setError('Failed to update bay status');
    }
  };

  const getRackName = (rackId) => {
    const rack = racks.find(r => r.id === rackId);
    return rack ? rack.name : rackId;
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : warehouseId;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BayIcon />
          Bay Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddBay}
        >
          Add Bay
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}


      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : bays.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <BayIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Bays Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create bays to organize inventory within racks
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddBay}
            >
              Create Bay
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bays.map((bay) => (
                <TableRow key={bay.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BayIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2">{bay.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bay.isActive ? 'Active' : 'Inactive'}
                      color={bay.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={bay.itemsCount || 0} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Toggle Active Status">
                      <Switch
                        checked={bay.isActive !== false}
                        onChange={() => handleToggleActive(bay)}
                        size="small"
                      />
                    </Tooltip>
                    <Tooltip title="Edit Bay">
                      <IconButton
                        size="small"
                        onClick={() => handleEditBay(bay)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Bay">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteBay(bay)}
                        disabled={bay.itemsCount > 0}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bay Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          setDialogOpen(false);
          setEditingBay(null);
          setFormData({ name: '', description: '', rack_id: '', warehouse_id: '', isActive: true });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingBay ? 'Edit Bay' : 'Add New Bay'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Bay Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              error={!formData.name && formData.name !== ''}
              helperText={!formData.name && formData.name !== '' ? 'Bay name is required' : ''}
              placeholder="Enter bay name (e.g., A, B, C, 1, 2, etc.)"
            />
            
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setEditingBay(null);
            setFormData({ name: '', description: '', rack_id: '', warehouse_id: '', isActive: true });
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveBay}
            variant="contained"
            disabled={!formData.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the bay "{bayToDelete?.name}"?
            {bayToDelete?.itemsCount > 0 && (
              <Box sx={{ mt: 1, color: 'error.main' }}>
                This bay contains {bayToDelete.itemsCount} items. You must reassign these items before deleting.
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={bayToDelete?.itemsCount > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}